"use client";

import { useCallback, useState, type CSSProperties } from "react";

const MAX_EXPR = 48;

/** Only digits and basic arithmetic — no identifiers, so evaluation stays bounded. */
function safeEvaluate(raw: string): string {
  const t = raw.replace(/\s/g, "");
  if (!t) return "0";
  if (t.length > MAX_EXPR) return "Error";
  if (!/^[0-9+\-*/.()]+$/.test(t)) return "Error";
  try {
    const fn = new Function(`"use strict"; return (${t})`);
    const v = fn();
    if (typeof v !== "number" || !Number.isFinite(v)) return "Error";
    if (Object.is(v, -0)) return "0";
    return formatNumber(v);
  } catch {
    return "Error";
  }
}

function formatNumber(n: number): string {
  if (Number.isInteger(n) && Math.abs(n) <= Number.MAX_SAFE_INTEGER) return String(n);
  const rounded = Math.round(n * 1e12) / 1e12;
  return String(rounded);
}

const KEYS: string[][] = [
  ["C", "(", ")", "/"],
  ["7", "8", "9", "*"],
  ["4", "5", "6", "-"],
  ["1", "2", "3", "+"],
  ["0", ".", "⌫", "="],
];

export function ExpiryCalculatorClient() {
  const [expr, setExpr] = useState("");

  const append = useCallback((ch: string) => {
    if (ch === "C") {
      setExpr("");
      return;
    }
    if (ch === "⌫") {
      setExpr((e) => e.slice(0, -1));
      return;
    }
    if (ch === "=") {
      setExpr((e) => safeEvaluate(e));
      return;
    }
    setExpr((e) => {
      if (e === "Error") {
        if (/[0-9]/.test(ch)) return ch;
        if (ch === "(") return "(";
        if (ch === "-") return "-";
        if (ch === ".") return "0.";
        return e;
      }
      if (e.length >= MAX_EXPR) return e;
      return e + ch;
    });
  }, []);

  const display = expr === "" ? "0" : expr;

  const btnStyle = (label: string): CSSProperties => ({
    padding: "0.65rem 0.5rem",
    fontSize: label === "=" ? "1.05rem" : "0.95rem",
    fontWeight: 700,
    fontFamily: "inherit",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    background:
      label === "=" ? "linear-gradient(180deg, #0ea5e9 0%, #0284c7 100%)" : label === "C" ? "#f1f5f9" : "#ffffff",
    color: label === "=" ? "#ffffff" : "#0c4a6e",
    cursor: "pointer",
    minHeight: "2.65rem",
  });

  return (
    <div className="ed-tool-card">
      <div
        role="status"
        aria-live="polite"
        aria-label="Expression"
        className="ed-tool-field"
        style={{
          display: "block",
          marginBottom: "1rem",
          fontSize: "1.25rem",
          fontWeight: 800,
          letterSpacing: "0.02em",
          textAlign: "right",
          minHeight: "2.75rem",
          lineHeight: 1.35,
          wordBreak: "break-all",
        }}
      >
        {display}
      </div>
      <div
        role="group"
        aria-label="Calculator keypad"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "0.45rem",
        }}
      >
        {KEYS.flat().map((k) => (
          <button key={k} type="button" style={btnStyle(k)} onClick={() => append(k)}>
            {k}
          </button>
        ))}
      </div>
    </div>
  );
}
